import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardDesignParameters, StandardDynamicComponent, StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { TranslateService } from '@ngx-translate/core';
import { DeviceTimers, DeviceTimersInfo } from 'src/app/model/device-timer';
import { selectCurrentDeviceCommands, selectCurrentDeviceModel, selectDeviceTimerInfo, selectDeviceTimerModify } from 'src/app/store/device/device.selector';
import { CustomValidators } from 'src/app/validators/custom.validators';
import { WEEK_DAYS_TIMERS } from 'src/app/conf/timers';
import moment from 'moment';
import { ControlItem } from 'src/app/model/control-item';
import { DeviceModel } from 'src/app/model/device-model';
import { EDeviceModel } from 'src/app/conf/device-models';
import { Command } from 'src/app/model/command';

@Component({
	selector: 'app-device-timer-dialog',
	templateUrl: './device-timer-dialog.component.html',
	providers: [{ provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true } }]
})

export class DeviceTimerDialogComponent implements OnInit, OnDestroy {
	timerModify$: Observable<boolean | undefined>;
	timerModifySub: Subscription | undefined;
	timersInfo$: Observable<DeviceTimersInfo | undefined>;
	timersInfoSub: Subscription | undefined;
	deviceCurrentCommands$: Observable<Command[] | undefined>;
	deviceCurrentCommandsSub: Subscription | undefined;
	deviceModel$: Observable<DeviceModel | undefined>;
	deviceModelSub: Subscription | undefined;

	timerForm: FormGroup;
	timer: DeviceTimers;
	controlItemTimer: ControlItem;
	deviceModel: DeviceModel;

	daysOfWeek = WEEK_DAYS_TIMERS;
	currentDeviceCommands: Command[];

	adding = true;
	commands: any[] = [];

	saving = false;

	// design
	screenWidth: number;
	MAX_MD_WIDTH = StandardDesignParameters.MAX_MD_WIDTH;

	constructor(
		private fb: FormBuilder,
		private store: Store<AppState>,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
		private translate: TranslateService,
	) {
		if (config.data !== undefined && config.data.timer !== undefined) {
			this.timer = config.data.timer;
			this.adding = false;
		} else {
			this.timer = new DeviceTimers();
		}
		this.initSelectors();
	}

	initSelectors(): void {
		this.timerModify$ = this.store.select(selectDeviceTimerModify);
		this.timersInfo$ = this.store.select(selectDeviceTimerInfo);
		this.deviceCurrentCommands$ = this.store.select(selectCurrentDeviceCommands);
		this.deviceModel$ = this.store.select(selectCurrentDeviceModel);
	}

	ngOnInit(): void {
		this.subscribeAll();
		this.buildForm();

		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	subscribeAll() {
		this.timerModifySub = this.timerModify$.pipe(filter(mtmaContactModifySub => !!mtmaContactModifySub))
			.subscribe(() => {
				this.resetClose();
			});
		this.timersInfoSub = this.timersInfo$.subscribe(timersInfo => {
			if (timersInfo !== undefined && timersInfo.ControlItem !== undefined) {
				this.commands = timersInfo.ControlItem.Items;

				for (const command of this.commands) {
					command.DescriptionItem = this.getDescriptionItem(command);
				}

			}
		});
		this.deviceCurrentCommandsSub = this.deviceCurrentCommands$.subscribe(deviceCommands => {
			this.currentDeviceCommands = deviceCommands;

			for (const command of this.commands) {
				command.DescriptionItem = this.getDescriptionItem(command);
			}
			if (this.adding && this.commands != undefined && this.commands.length > 0) {
				this.timer.Mode = this.commands[0].Value;
				this.f.selCommands.value = this.timer.Mode;
			}
		});
		this.deviceModelSub = this.deviceModel$.subscribe(deviceModel => {
			this.deviceModel = deviceModel;

			for (const command of this.commands) {
				command.DescriptionItem = this.getDescriptionItem(command);
			}
		});
	}

	buildForm() {
		//const currentWeekDays = this.schedule.WeekDays ? this.schedule.WeekDays.split(',') : [];
		const currentWeekDays = this.getDays();
		const wDayArr = WEEK_DAYS_TIMERS.map((weekday) => this.fb.control(currentWeekDays.includes(weekday.Value)));
		this.timerForm = new FormGroup({
			'weekDays': this.fb.array(wDayArr, CustomValidators.multipleCheckboxRequireOne),
			'startTime': new FormControl(this.getTime(this.timer.StartHour, this.timer.StartMin), Validators.required),
			'endTime': new FormControl(this.getTime(this.timer.EndHour, this.timer.EndMin), Validators.required),
			'selCommands': new FormControl(this.timer.Mode, Validators.required),
		});
		this.timerForm.addValidators(CustomValidators.checkCorrectTimeSequence('startTime', 'endTime'));
		/*if (this.timer.Mode && this.timer.Mode === 1) {
			this.timer.Mode = 16; // Output 1
			this.f.selCommands.value = this.timer.Mode;
		}*/
	}

	ngOnDestroy(): void {
		this.timerModifySub?.unsubscribe();
		this.timersInfoSub?.unsubscribe();
		this.deviceCurrentCommandsSub?.unsubscribe();
		this.deviceModelSub?.unsubscribe();
	}

	resetClose(): void {
		this.store.dispatch(DeviceActions.resetDeviceTimerForm());
		this.closeDialog();
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.timerForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.timerForm.get(key).errors;
			if (controlErrors != null) {
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	save(): void {
		this.saving = true;
		// START
		if (typeof this.f.startTime.value === 'string') {
			this.timer.StartHour = Number(this.f.startTime.value.split(':')[0]);
			this.timer.StartMin = Number(this.f.startTime.value.split(':')[1]);
		} else {
			this.timer.StartHour = +moment(this.f.startTime.value).hours();
			this.timer.StartMin = +moment(this.f.startTime.value).minutes();
		}
		// END
		if (typeof this.f.endTime.value === 'string') {
			this.timer.EndHour = Number(this.f.endTime.value.split(':')[0]);
			this.timer.EndMin = Number(this.f.endTime.value.split(':')[1]);
		} else {
			this.timer.EndHour = +moment(this.f.endTime.value).hours();
			this.timer.EndMin = +moment(this.f.endTime.value).minutes();
		}
		this.timer = {
			...this.timer,
			Mode: this.f.selCommands.value,
			WeekMask: this.parseDays(),
			State: 170,
		};
		if (this.adding) {
			this.store.dispatch(DeviceActions.addDeviceTimer({ timer: this.timer }));
		} else {
			this.store.dispatch(DeviceActions.editDeviceTimer({ timer: this.timer }));
		}
	}

	getDays(): number[] {
		const list = [];
		let dayBitmask = this.timer.WeekMask;
		for (let i = 7; i >= 0; i--) {
			if (Math.floor(dayBitmask / (2 ** i)) === 1) {
				dayBitmask = dayBitmask - (2 ** i);
				list.unshift(i);
			}
		}
		return list;
	}

	parseDays(): number {
		const WDControl = this.f.weekDays as FormArray;
		const days = [];
		let value = 0;
		WDControl.controls.forEach((c, i) => {
			if (c.value) {
				days.push(i);
				value = value + 2 ** i;
			}
		});
		return value;
	}

	isRbe24230(device: DeviceModel): boolean {
		return device !== undefined && (device.Id === EDeviceModel.RBE24230 || device.Id === EDeviceModel.RBE230 ||
			device.Id === EDeviceModel.RBE24 || device.Id === EDeviceModel.GEN_RBE24230);
	}

	getDescriptionItem(item: any): string {
		if (item === undefined) {
			return '';
		}
		if (this.isRbe24230(this.deviceModel)) {
			for (const command of this.currentDeviceCommands) {
				if (item.Value === 16 && command.Id === 144) {
					return command.Default === command.Description ?
						command.Description : command.Description + ' (' + command.Default + ')';
				}
				if (item.Value === 32 && command.Id === 145) {
					return command.Default === command.Description ?
						command.Description : command.Description + ' (' + command.Default + ')';
				}
				if (item.Value === 64 && command.Id === 146) {
					return command.Default === command.Description ?
						command.Description : command.Description + ' (' + command.Default + ')';
				}
				if (item.Value === 128 && command.Id === 147) {
					return command.Default === command.Description ?
						command.Description : command.Description + ' (' + command.Default + ')';
				}
			}
			return item.Description;
		} else {
			return item.Description;
		}
	}

	getTime(hour: number, minute: number): string {
		return moment().hour(hour).minute(minute).format('HH:mm');
	}

	get f(): any {
		return this.timerForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: DeviceTimerDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: DeviceTimerDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	dynamicDatepicker(): string {
		return StandardDynamicComponent.dynamicDatepicker(this.screenWidth);
	}

	dynamicInputTextDesktopTabletWidth(): string {
		return StandardPageLayout.dynamicInputTextDesktopTabletWidth(this.screenWidth, 'w-20rem', 'w-30rem');
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

	dynamicInputShortTextWidth(): string {
		return StandardPageLayout.dynamicInputTextDesktopTabletWidth(this.screenWidth, 'w-20rem', 'w-30rem');
	}

	manageRespStylewithError(type: DeviceTimerDialogComponent["f"], classType: string): string {
		return StandardNewValidator.manageRespStylewithError(type, classType, this.screenWidth);
	}

	showErrorRequiredCheckBox(type: DeviceTimerDialogComponent["f"]): string {
		return StandardNewValidator.showErrorRequiredCheckBox(type);
	}
}
